import React, { useEffect, useMemo, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import * as THREE from 'three';

function makeFaceTexture(symbol, bg, accent) {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 512;
    const ctx = canvas.getContext('2d');

    const grad = ctx.createRadialGradient(256, 256, 30, 256, 256, 256);
    grad.addColorStop(0, bg);
    grad.addColorStop(1, '#04040f');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(256, 256, 256, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = accent;
    ctx.shadowColor = accent;
    ctx.shadowBlur = 40;
    ctx.lineWidth = 10;
    ctx.beginPath();
    ctx.arc(256, 256, 235, 0, Math.PI * 2);
    ctx.stroke();

    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(256, 256, 210, 0, Math.PI * 2);
    ctx.stroke();

    ctx.shadowBlur = 15;
    ctx.strokeStyle = accent + '55';
    ctx.lineWidth = 1;
    for (let i = 0; i < 24; i++) {
        const a = (i / 24) * Math.PI * 2;
        const x1 = 256 + Math.cos(a) * 220;
        const y1 = 256 + Math.sin(a) * 220;
        const x2 = 256 + Math.cos(a) * 235;
        const y2 = 256 + Math.sin(a) * 235;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
    }

    ctx.shadowBlur = 60;
    ctx.fillStyle = accent;
    ctx.font = 'bold 280px "Segoe UI Symbol", "Apple Color Emoji", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(symbol, 256, 270);

    const tex = new THREE.CanvasTexture(canvas);
    tex.anisotropy = 16;
    tex.needsUpdate = true;
    return tex;
}

function CoinMesh({ phase, result }) {
    const group = useRef();
    const currentAngle = useRef(0);
    const targetAngle = useRef(null);
    const spinSpeed = useRef(0);

    const [skullTex, boltTex] = useMemo(() => [
        makeFaceTexture('☠', '#120730', '#22e6ff'),
        makeFaceTexture('⚡', '#1a0530', '#b14bff'),
    ], []);

    useEffect(() => {
        if (phase === 'reveal' && (result === 0 || result === 1)) {
            const base = currentAngle.current;
            const fullTurns = 5 * Math.PI * 2;
            const offset = result === 0 ? 0 : Math.PI;
            const currentMod = ((base % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
            const delta = ((offset - currentMod) + Math.PI * 2) % (Math.PI * 2);
            targetAngle.current = base + fullTurns + delta;
        } else if (phase !== 'reveal') {
            targetAngle.current = null;
        }
    }, [phase, result]);

    useFrame((_, dt) => {
        if (!group.current) return;
        const t = performance.now() * 0.001;

        if (phase === 'idle') {
            spinSpeed.current = 0;
            currentAngle.current += dt * 0.6;
            group.current.rotation.x = currentAngle.current;
            group.current.rotation.z = Math.sin(t * 0.8) * 0.08;
            group.current.position.y = Math.sin(t * 1.5) * 0.08;
        } else if (phase === 'spinning') {
            spinSpeed.current = Math.min(spinSpeed.current + dt * 30, 22);
            currentAngle.current += dt * spinSpeed.current;
            group.current.rotation.x = currentAngle.current;
            group.current.rotation.z = Math.sin(t * 4) * 0.15;
            group.current.position.y = Math.abs(Math.sin(t * 6)) * 0.4;
        } else if (phase === 'reveal') {
            if (targetAngle.current !== null) {
                const diff = targetAngle.current - currentAngle.current;
                currentAngle.current += diff * Math.min(dt * 3.5, 1);
            } else {
                currentAngle.current += dt * 0.6;
            }
            group.current.rotation.x = currentAngle.current;
            group.current.rotation.z *= 0.9;
            group.current.position.y *= 0.88;
        }
    });

    return (
        <group ref={group}>
            <mesh rotation={[Math.PI / 2, 0, 0]}>
                <cylinderGeometry args={[1.3, 1.3, 0.18, 96, 1, false]} />
                <meshStandardMaterial
                    attach="material-0"
                    color="#1a1a3a"
                    metalness={0.95}
                    roughness={0.2}
                    emissive="#0a0a20"
                />
                <meshStandardMaterial
                    attach="material-1"
                    map={skullTex}
                    metalness={0.7}
                    roughness={0.25}
                    emissive="#22e6ff"
                    emissiveMap={skullTex}
                    emissiveIntensity={0.6}
                />
                <meshStandardMaterial
                    attach="material-2"
                    map={boltTex}
                    metalness={0.7}
                    roughness={0.25}
                    emissive="#b14bff"
                    emissiveMap={boltTex}
                    emissiveIntensity={0.6}
                />
            </mesh>
            <mesh rotation={[Math.PI / 2, 0, 0]}>
                <torusGeometry args={[1.32, 0.02, 12, 96]} />
                <meshBasicMaterial color="#22e6ff" />
            </mesh>
        </group>
    );
}

export default function Coin3D({ phase, result, flexible }) {
    return (
        <div className={`w-full ${flexible ? 'h-full' : 'h-[220px] sm:h-[340px] md:h-[420px]'}`}>
            <Canvas
                camera={{ position: [0, 0.5, 4.5], fov: 45 }}
                dpr={[1, 2]}
                gl={{ antialias: true, alpha: true }}
            >
                <color attach="background" args={['#00000000']} />
                <ambientLight intensity={0.25} />
                <directionalLight position={[5, 5, 5]} intensity={0.8} color="#ffffff" />
                <pointLight position={[-4, 2, 3]} intensity={3} color="#22e6ff" distance={15} />
                <pointLight position={[4, -2, 3]} intensity={3} color="#b14bff" distance={15} />
                <pointLight position={[0, 0, 5]} intensity={1.2} color="#ff2bd6" distance={10} />
                <CoinMesh phase={phase} result={result} />
            </Canvas>
        </div>
    );
}
